import { flow, types } from 'mobx-state-tree';
import moment from 'moment-timezone';
import { MENU_AUTH_LIST, SYSTEM_NAME, TMS_ORGANIZATION_ID_KEY, TMS_TOKEN_KEY } from '../../constants';
import { getHereMapKey, getUserInfo } from '../../services/api/user';
import { getBusinessSetting } from '../../services/api/notice';
import { IOrganization, IUserInfo, IUserInfoResolve } from '../../services/api/user.types';
import storage from '../../utils/storage';
import arms from '@/utils/arms';

const Organization = types.model('Organization', {
  Id: types.identifier,
  Name: types.string,
  FullName: types.string,
  TimezoneId: types.string,
  LogoUrl: types.maybeNull(types.string),
});
const Organizations = types.array(Organization);

export const checkTokenExp = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    const decodedToken = JSON.parse(atob(base64));
    if (decodedToken && decodedToken.exp) {
      return decodedToken.exp > new Date().valueOf() / 1000;
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const UserInfoStoreModel = types
  .model('UserInfoStoreModel')
  .props({
    token: types.optional(types.string, ''),
    userName: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    userId: types.optional(types.string, ''),
    organizationId: types.optional(types.string, ''),
    logoName: types.optional(types.string, ''),
    logoUrl: types.maybeNull(types.string),
    organizations: types.optional(Organizations, []),
    hereMapKey: types.optional(types.string, ''),
    unreadCount: types.optional(types.number, 0),
    markerRemarUnreadCount: types.optional(types.number, 0),
    activeAlertCount: types.optional(types.number, 0),
    isAudit: types.optional(types.boolean, false),
    isChangePassword: types.optional(types.boolean, false),
    isLockDeviceAfterPasswordFailures: types.optional(types.boolean, false),
    isIdleLogoutEnabled: types.optional(types.boolean, false),
    isMaskSensitiveData: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get organization() {
      if (self.organizationId) {
        return self.organizations.filter((item) => item.Id === self.organizationId);
      } else {
        return null;
      }
    },
    get timezoneId() {
      if (this.organization && this.organization?.length > 0) {
        return this.organization[0].TimezoneId;
      }
      return moment.tz.guess(true);
    },
  }))
  .actions((self) => {
    const setToken = (token: string) => {
      self.token = `Bearer ${token}`;
      storage.localSet(TMS_TOKEN_KEY, token);
    };
    const setOrganizations = (organizations: IOrganization[]) => {
      self.organizations = Organizations.create(organizations);
    };
    const updateUserInfo = (user: IUserInfo) => {
      self.userName = user.UserName;
      self.email = user.Email || '';
      self.userId = user.UserId;
      self.isAudit = user.IsAudit;
      self.isChangePassword = user.IsChangePassword;
      arms.setConfig({
        uid: user.UserId,
        setUsername: () => {
          return user.UserName || null;
        },
      });
      setOrganizations(user.Organizations);
      const org = user.Organizations.find(item => item.Id === self.organizationId);
      setLogoUrl(org ? org.LogoUrl : null);
    };
    const logout = () => {
      self.userName = '';
      self.email = '';
      self.userId = '';
      self.token = '';
      self.organizationId = '';
      self.organizations = Organizations.create([]);
      self.isAudit = false;
      self.isLockDeviceAfterPasswordFailures = false;
      self.isIdleLogoutEnabled = false;
      self.isMaskSensitiveData = false;
      storage.localRemove(TMS_ORGANIZATION_ID_KEY);
      storage.localRemove(TMS_TOKEN_KEY);
      self.hereMapKey = '';
    };
    const setIsChangePassword = (value: boolean) => {
      self.isChangePassword = value;
    };
    const requestUserInfo = flow(function* requestUserInfo() {
      const { Data }: IUserInfoResolve = yield getUserInfo();
      const menClaimModels = Data.MenClaimModels || [];
      const menuAuthList = menClaimModels.map(item => item.MenName.toUpperCase());
      storage.localSet(MENU_AUTH_LIST, menuAuthList);
      window.refreshMenuAuthList?.(menuAuthList);
      updateUserInfo(Data);
    });
    const requestHereKey = flow(function* requestHereKey() {
      const { Data } = yield getHereMapKey();
      self.hereMapKey = Data;
    });
    const requestBusinessSetting = flow(function* requestBusinessSetting() {
      try {
        const { Data } = yield getBusinessSetting();
        const businessSetting = Data?.BusinessSetting;
        if (businessSetting) {
          self.isLockDeviceAfterPasswordFailures = Boolean(businessSetting.IsLockDeviceAfterPasswordFailures);
          self.isIdleLogoutEnabled = Boolean(businessSetting.IsIdleLogoutEnabled);
          self.isMaskSensitiveData = Boolean(businessSetting.IsMaskSensitiveData);
        }
      } catch (error) {
        // 獲取失敗時保持默認值（均為 false）
      }
    });
    const setOrganization = (organizationId: string) => {
      self.organizationId = organizationId;
      storage.localSet(TMS_ORGANIZATION_ID_KEY, organizationId);
    };
    const setLogoUrl = (url: string|null) => {
      self.logoUrl = url;
      self.logoName = url ? '' : SYSTEM_NAME;
    };
    // 钩子函数
    const afterCreate = () => {
      const token = storage.localGet(TMS_TOKEN_KEY);
      const organizationId = storage.localGet(TMS_ORGANIZATION_ID_KEY);
      if (token && typeof token === 'string' && checkTokenExp(token)) {
        setToken(token);
      } else {
        storage.localRemove(TMS_TOKEN_KEY);
      }
      if (organizationId && typeof organizationId === 'string') {
        setOrganization(organizationId);
      }
    };
    const setUnreadCount = (value: number) => {
      self.unreadCount = value;
    };

    const setMarkerRemarUnreadCount = (value: number) => {
      self.markerRemarUnreadCount = value;
    };
    const setActiveAlertCount = (value: number) => {
      self.activeAlertCount = value;
    };

    return {
      setToken,
      updateUserInfo,
      logout,
      requestUserInfo,
      requestHereKey,
      requestBusinessSetting,
      afterCreate,
      setOrganization,
      setLogoUrl,
      setOrganizations,
      setUnreadCount,
      setMarkerRemarUnreadCount,
      setActiveAlertCount,
      setIsChangePassword,
    };
  });
export const userInfoStore = UserInfoStoreModel.create();
